/**
 * 图案裁剪手动框选：展示坐标 ↔ 归一化 bbox（0–1000）换算。
 */

const MIN_BOX_PX = 24;
const DEFAULT_HANDLE_SIZE = 32;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function roundInt(value) {
  return Math.round(Number(value) || 0);
}

/** 图片在容器内居中展示的矩形（像素） */
export function computeImageDisplayRect(containerWidth, containerHeight, naturalWidth, naturalHeight) {
  const cw = Math.max(0, Number(containerWidth) || 0);
  const ch = Math.max(0, Number(containerHeight) || 0);
  const nw = Math.max(1, Number(naturalWidth) || 1);
  const nh = Math.max(1, Number(naturalHeight) || 1);

  const imageAspect = nw / nh;
  let displayWidth = cw;
  let displayHeight = cw / imageAspect;

  if (displayHeight > ch) {
    displayHeight = ch;
    displayWidth = ch * imageAspect;
  }

  const offsetX = (cw - displayWidth) / 2;
  const offsetY = (ch - displayHeight) / 2;

  return {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
    naturalWidth: nw,
    naturalHeight: nh,
  };
}

/**
 * 在可视区域内 fit 展示图片，并按 zoom 放大；超出视口时可滚动。
 */
export function computeZoomedImageDisplayRect(
  viewportWidth,
  viewportHeight,
  naturalWidth,
  naturalHeight,
  zoom = 1,
) {
  const fitRect = computeImageDisplayRect(viewportWidth, viewportHeight, naturalWidth, naturalHeight);
  const scale = clamp(Number(zoom) || 1, 0.5, 3);
  const displayWidth = fitRect.displayWidth * scale;
  const displayHeight = fitRect.displayHeight * scale;

  const offsetX = displayWidth <= viewportWidth
    ? (viewportWidth - displayWidth) / 2
    : 0;
  const offsetY = displayHeight <= viewportHeight
    ? (viewportHeight - displayHeight) / 2
    : 0;

  return {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
    naturalWidth: fitRect.naturalWidth,
    naturalHeight: fitRect.naturalHeight,
    canvasWidth: Math.max(viewportWidth, displayWidth),
    canvasHeight: Math.max(viewportHeight, displayHeight),
    scale,
  };
}

/** 归一化 bbox → 容器内裁剪框像素坐标 */
export function normalizedBboxToDisplayCropBox(bbox, imageRect) {
  if (!Array.isArray(bbox) || bbox.length !== 4 || !imageRect) return null;
  const nums = bbox.map((v) => Number(v));
  if (nums.some((n) => !Number.isFinite(n))) return null;

  const [xMin, yMin, xMax, yMax] = nums;
  if (xMin >= xMax || yMin >= yMax) return null;

  const {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
  } = imageRect;

  return {
    x: offsetX + (xMin / 1000) * displayWidth,
    y: offsetY + (yMin / 1000) * displayHeight,
    width: ((xMax - xMin) / 1000) * displayWidth,
    height: ((yMax - yMin) / 1000) * displayHeight,
  };
}

/** 容器内裁剪框 → 归一化 bbox；越界部分会 clamp 到图片区域 */
export function displayCropBoxToNormalizedBbox(cropBox, imageRect) {
  if (!cropBox || !imageRect) return null;

  const {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
  } = imageRect;

  const relX = cropBox.x - offsetX;
  const relY = cropBox.y - offsetY;
  const relRight = relX + cropBox.width;
  const relBottom = relY + cropBox.height;

  const clampedLeft = clamp(relX, 0, displayWidth);
  const clampedTop = clamp(relY, 0, displayHeight);
  const clampedRight = clamp(relRight, 0, displayWidth);
  const clampedBottom = clamp(relBottom, 0, displayHeight);

  if (clampedRight - clampedLeft < 1 || clampedBottom - clampedTop < 1) {
    return null;
  }

  const xMin = roundInt((clampedLeft / displayWidth) * 1000);
  const yMin = roundInt((clampedTop / displayHeight) * 1000);
  const xMax = roundInt((clampedRight / displayWidth) * 1000);
  const yMax = roundInt((clampedBottom / displayHeight) * 1000);

  if (xMin >= xMax || yMin >= yMax) return null;
  return [xMin, yMin, xMax, yMax];
}

/** 无历史 bbox 时，在图片区域中央生成默认框（约 70% 短边） */
export function createDefaultDisplayCropBox(imageRect, ratio = 0.7) {
  if (!imageRect) return null;
  const {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
  } = imageRect;

  const size = Math.max(MIN_BOX_PX, Math.min(displayWidth, displayHeight) * ratio);
  const width = Math.min(size, displayWidth);
  const height = Math.min(size, displayHeight);

  return {
    x: offsetX + (displayWidth - width) / 2,
    y: offsetY + (displayHeight - height) / 2,
    width,
    height,
  };
}

/** 将裁剪框限制在图片展示区域内 */
export function clampCropBoxToImage(cropBox, imageRect, minSize = MIN_BOX_PX) {
  if (!cropBox || !imageRect) return cropBox;

  const min = Math.max(8, Number(minSize) || MIN_BOX_PX);
  const {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
  } = imageRect;

  const maxX = offsetX + displayWidth;
  const maxY = offsetY + displayHeight;

  let width = Math.max(min, Math.min(cropBox.width, displayWidth));
  let height = Math.max(min, Math.min(cropBox.height, displayHeight));

  let x = clamp(cropBox.x, offsetX, maxX - width);
  let y = clamp(cropBox.y, offsetY, maxY - height);

  if (x + width > maxX) width = maxX - x;
  if (y + height > maxY) height = maxY - y;

  return { x, y, width, height };
}

/** 根据裁剪框与图片尺寸计算输出像素宽高 */
export function resolveCropBoxPixelSize(cropBox, imageRect) {
  const bbox = displayCropBoxToNormalizedBbox(cropBox, imageRect);
  if (!bbox || !imageRect) {
    return { width: 0, height: 0, bbox: null };
  }

  const [xMin, yMin, xMax, yMax] = bbox;
  const { naturalWidth, naturalHeight } = imageRect;
  const width = Math.max(1, roundInt(((xMax - xMin) / 1000) * naturalWidth));
  const height = Math.max(1, roundInt(((yMax - yMin) / 1000) * naturalHeight));
  return { width, height, bbox };
}

export const CROP_ADJUST_MIN_BOX_PX = MIN_BOX_PX;

export const CROP_RESIZE_HANDLES = [
  'tl', 'tr', 'bl', 'br', 't', 'b', 'l', 'r',
];

export const CROP_ADJUST_HANDLE_SIZE = DEFAULT_HANDLE_SIZE;

/** 命中检测：返回 handle id 或 'move' */
export function hitTestCropBox(localX, localY, cropBox, handleSize = DEFAULT_HANDLE_SIZE) {
  if (!cropBox) return null;
  const hs = handleSize / 2;
  const { x, y, width, height } = cropBox;
  const right = x + width;
  const bottom = y + height;
  const midX = x + width / 2;
  const midY = y + height / 2;

  const near = (px, py, cx, cy) => Math.abs(px - cx) <= hs && Math.abs(py - cy) <= hs;

  if (near(localX, localY, x, y)) return 'tl';
  if (near(localX, localY, right, y)) return 'tr';
  if (near(localX, localY, x, bottom)) return 'bl';
  if (near(localX, localY, right, bottom)) return 'br';
  if (Math.abs(localY - y) <= hs && localX >= x && localX <= right) return 't';
  if (Math.abs(localY - bottom) <= hs && localX >= x && localX <= right) return 'b';
  if (Math.abs(localX - x) <= hs && localY >= y && localY <= bottom) return 'l';
  if (Math.abs(localX - right) <= hs && localY >= y && localY <= bottom) return 'r';

  if (localX >= x && localX <= right && localY >= y && localY <= bottom) {
    return 'move';
  }
  return null;
}

/** 根据拖拽更新裁剪框 */
export function applyCropBoxResize(handle, startBox, dx, dy, imageRect, minSize = MIN_BOX_PX) {
  if (!handle || !startBox || !imageRect) return startBox;

  const min = Math.max(8, Number(minSize) || MIN_BOX_PX);
  const {
    offsetX,
    offsetY,
    displayWidth,
    displayHeight,
  } = imageRect;
  const maxX = offsetX + displayWidth;
  const maxY = offsetY + displayHeight;

  let { x, y, width, height } = startBox;

  if (handle === 'move') {
    x = startBox.x + dx;
    y = startBox.y + dy;
    return clampCropBoxToImage({ x, y, width, height }, imageRect, min);
  }

  const right = startBox.x + startBox.width;
  const bottom = startBox.y + startBox.height;

  if (handle.includes('l')) {
    x = clamp(startBox.x + dx, offsetX, right - min);
    width = right - x;
  }
  if (handle.includes('r')) {
    const newRight = clamp(right + dx, startBox.x + min, maxX);
    width = newRight - startBox.x;
  }
  if (handle.includes('t')) {
    y = clamp(startBox.y + dy, offsetY, bottom - min);
    height = bottom - y;
  }
  if (handle.includes('b')) {
    const newBottom = clamp(bottom + dy, startBox.y + min, maxY);
    height = newBottom - startBox.y;
  }

  if (handle === 't') {
    x = startBox.x;
    width = startBox.width;
  } else if (handle === 'b') {
    x = startBox.x;
    width = startBox.width;
  } else if (handle === 'l') {
    y = startBox.y;
    height = startBox.height;
  } else if (handle === 'r') {
    y = startBox.y;
    height = startBox.height;
  }

  return clampCropBoxToImage({ x, y, width, height }, imageRect, min);
}
